import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { getScrollContainer } from '@theme/scroll-container';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    target?.addEventListener('pointerdown', this.#capturePickerPosition, { signal, capture: true });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #pickerPosition;

  #capturePickerPosition = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('variant-picker')) return;
    const container = getScrollContainer();
    this.#pickerPosition = { top: container.scrollTop, time: performance.now() };
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const container = getScrollContainer();
    const captured = this.#pickerPosition;
    const scrollBefore = captured && performance.now() - captured.time < 1000 ? captured.top : container.scrollTop;
    const pickerBefore = this.closest('.product-information')?.querySelector('variant-picker');
    const before = pickerBefore?.getBoundingClientRect();
    const previousAnchor = container.style.overflowAnchor;
    const keepPosition = before && before.bottom > 0 && before.top < innerHeight;
    if (keepPosition) container.style.overflowAnchor = 'none';

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery || !this.isConnected) return;
        if (this.dataset.hlMediaSet && this.dataset.hlMediaSet === newMediaGallery.dataset.hlMediaSet) return;
        const section = this.closest('.product-information');
        const picker = section?.querySelector('variant-picker');
        this.replaceWith(newMediaGallery);
        if (keepPosition && picker?.isConnected) {
          const mobile = window.matchMedia('(max-width: 989px)').matches;
          const delta = mobile ? picker.getBoundingClientRect().top - before.top : 0;
          const top = mobile ? scrollBefore + delta : scrollBefore;
          container.scrollTo({ top, behavior: 'instant' });
          requestAnimationFrame(() => {
            container.scrollTo({ top, behavior: 'instant' });
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      })
      .finally(() => {
        if (keepPosition) requestAnimationFrame(() => requestAnimationFrame(() => {
          container.style.overflowAnchor = previousAnchor;
        }));
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
